import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { getPublicConfig } from "../../api/config/getPublicConfig";
import { getDossier } from "../../api/dossier/getDossier";
import { getListQuotes } from "../../api/dossier/getListQuotes";
import { getQuoteComparisonDefaultCriteria } from "../../api/quote/getQuoteComparisonDefaultCriteria";
import { setQuoteComparisonDefaultCriteria } from "../../api/quote/setQuoteComparisonDefaultCriteria";
import APContactHelpButton from "../../components/buttons/APContactHelpButton";
import BackButton from "../../components/buttons/BackButton";
import APAutoComplete, {
  AutoCompleteOption,
} from "../../components/form/APAutoComplete";
import PageContainer from "../../components/PageContainer";
import RestrictedAppScope from "../../components/RestrictedAppScope";
import { isAPQuoteHideInvalidCalculationsEnabled } from "../../helpers/configuration";
import {
  buildQuoteComparisonQueryString,
  buildSelectableQuoteComparisonCriteriaOption,
  buildSelectableQuoteComparisonCriteriaOptions,
  filterQuotes,
  sortQuotes,
} from "../../helpers/quotes";
import { useAuth } from "../../providers/AuthProvider";
import { QuoteComparisonItem } from "../../types/Quote";
import QuoteList from "../components/QuoteList";

type CompareQuotesInputs = {
  quotesInComparison: string[];
  primaryCriteria: AutoCompleteOption | null;
  selectedCriteria: AutoCompleteOption[] | null;
};

const CompareQuotes: React.FC = () => {
  const { dossierId } = useParams<{ dossierId: string }>();
  const { getScopedPageUrl, inAppScope } = useAuth();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CompareQuotesInputs>({
    mode: "onTouched",
  });
  const [showFilters, setShowFilters] = useState(false);

  const primaryCriteria = watch("primaryCriteria");
  const selectedCriteria = watch("selectedCriteria");
  const quotesInComparison = watch("quotesInComparison");

  if (!dossierId) {
    // TODO: improve client side error messaging
    throw new Error("dossier id param is misisng");
  }

  const { mutateAsync: mutateAsyncDefaultCriteria } =
    setQuoteComparisonDefaultCriteria();

  const dossier = getDossier(dossierId);
  const quotes = getListQuotes(dossierId);
  const configMap = getPublicConfig();
  const defaultCriteria = getQuoteComparisonDefaultCriteria();

  if (dossier.isError) {
    throw dossier.error;
  }

  if (quotes.isError) {
    throw quotes.error;
  }

  const primaryQuoteId = dossier.data?.opportunity?.primaryQuoteId ?? null;
  const isInvalidCalculationsHidden =
    configMap.isSuccess &&
    isAPQuoteHideInvalidCalculationsEnabled(configMap.data);
  const sortedQuotes = quotes.isSuccess
    ? sortQuotes(
        filterQuotes(
          quotes.data,
          inAppScope("internal"),
          isInvalidCalculationsHidden
        ),
        "lastUpdated",
        primaryQuoteId
      )
    : [];

  useEffect(() => {
    if (sortedQuotes) {
      // Work around in case old criteria option "onRoadCost" is cached
      const _defaultSelectedCriteria =
        defaultCriteria.data?.selectedCriteria.map((criteria) =>
          (criteria as string) === "onRoadCost"
            ? "vehicle.onRoadCost"
            : criteria
        ) ?? undefined;
      const _defaultPrimaryCriteria =
        (defaultCriteria.data?.primaryCriteria as string) === "onRoadCost"
          ? "vehicle.onRoadCost"
          : defaultCriteria.data?.primaryCriteria ?? undefined;

      const selectedCriteria = _defaultSelectedCriteria ?? [
        "vehicle.onRoadCost",
        "vehicle.warrantYrs",
        "savingsPercentage",
        "annualSavings",
      ];
      const primaryCriteria = _defaultPrimaryCriteria ?? "weeklyNetCost";

      setValue(
        "quotesInComparison",
        sortedQuotes.slice(0, 5).map((quote) => quote.identifiers.crmQuoteId)
      );
      setValue(
        "selectedCriteria",
        selectedCriteria.map((criteria) =>
          buildSelectableQuoteComparisonCriteriaOption(criteria)
        )
      );
      setValue(
        "primaryCriteria",
        buildSelectableQuoteComparisonCriteriaOption(primaryCriteria)
      );

      if (defaultCriteria.data) {
        setShowFilters(true);
      }
    }
  }, [quotes.isFetching]);

  useEffect(() => {
    // If primary criteria is also in selected criteria, remove it
    if (primaryCriteria && selectedCriteria) {
      const newSelectedCriteria = selectedCriteria.filter(
        (criteria) => criteria.id !== primaryCriteria.id
      );
      setValue("selectedCriteria", newSelectedCriteria);
    }
  }, [primaryCriteria]);

  const onFiltersClick = () => {
    setShowFilters(!showFilters);
  };

  const onToggleCompare = async (quoteId: string) => {
    if (quotesInComparison) {
      const isIncluded = quotesInComparison.includes(quoteId);

      if (isIncluded) {
        const quoteIds = quotesInComparison.filter((id) => id !== quoteId);
        setValue("quotesInComparison", quoteIds);
      } else if (quotesInComparison.length < 5) {
        const quoteIds = [...quotesInComparison, quoteId];
        setValue("quotesInComparison", quoteIds);
      }
    }
  };

  const onSubmit: SubmitHandler<CompareQuotesInputs> = async (_data) => {
    const quoteIds = _data.quotesInComparison;
    const primaryCriteria = _data.primaryCriteria
      ?.id as keyof QuoteComparisonItem;
    const selectedCriteria =
      _data.selectedCriteria?.map(
        (option) => option.id as keyof QuoteComparisonItem
      ) ?? [];

    const queryString = buildQuoteComparisonQueryString(
      quoteIds,
      primaryCriteria,
      selectedCriteria
    );

    await mutateAsyncDefaultCriteria({
      primaryCriteria,
      selectedCriteria,
    });

    navigate(
      `${getScopedPageUrl("viewQuoteComparisons", dossierId)}?${queryString}`
    );
  };

  const compareButtonDisabled =
    !primaryCriteria || quotesInComparison.length < 2;

  const isLoading =
    quotes.isLoading || dossier.isLoading || configMap.isLoading;
  const isSuccess =
    quotes.isSuccess && dossier.isSuccess && configMap.isSuccess;

  return (
    <PageContainer
      loading={isLoading}
      title="Compare Quotes"
      titleButton={
        <IconButton
          size="large"
          aria-label="refresh"
          color="onSurface"
          onClick={onFiltersClick}
        >
          <SearchOutlinedIcon />
        </IconButton>
      }
    >
      {isSuccess && (
        <Stack spacing={2}>
          <Box>
            <Typography variant="labelLarge">
              Select up to 5 quotes then click compare.
            </Typography>
          </Box>
          <Collapse in={showFilters} sx={{ p: 1 }}>
            <Stack spacing={2}>
              <APAutoComplete
                name="primaryCriteria"
                label="Primary Criteria"
                options={buildSelectableQuoteComparisonCriteriaOptions()}
                control={control}
                validations={{}}
                errors={errors}
                defaultValue={""}
              />

              <APAutoComplete
                name="selectedCriteria"
                label="Criteria (max 4)"
                options={buildSelectableQuoteComparisonCriteriaOptions(
                  primaryCriteria?.id as keyof QuoteComparisonItem
                )}
                control={control}
                validations={{}}
                errors={errors}
                defaultValue={[]}
                setValue={setValue}
                multiple
                disableSelectAll
                maxSelected={4}
              />
            </Stack>
          </Collapse>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              size="large"
              color="secondary"
              variant="contained"
              disableElevation={true}
              startIcon={<PlayArrowOutlinedIcon />}
              onClick={handleSubmit(onSubmit)}
              disabled={compareButtonDisabled}
            >
              Compare
            </Button>
          </Box>
          <Box>
            <QuoteList
              quotes={sortedQuotes}
              primaryQuoteId={primaryQuoteId}
              actions={{
                actionHandlers: {
                  onToggleCompare: onToggleCompare,
                },
                topActions: ["onToggleCompare", "onSetPrimary"],
                onCardClick: onToggleCompare,
              }}
              config={{
                disableCardLink: true,
                hidePrimaryToggle: true,
                hidePricing: true,
                quotesInComparison: quotesInComparison,
              }}
            />
          </Box>
          <Box
            sx={{
              pt: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Stack
              spacing={2}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <Box display="flex" gap={1}>
                <BackButton />
              </Box>
              <RestrictedAppScope scope="external">
                <Box>
                  <APContactHelpButton
                    regardingOpportunityId={
                      dossier.data.opportunity?.id ?? undefined
                    }
                  />
                </Box>
              </RestrictedAppScope>
            </Stack>
            <Stack
              spacing={1}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <Button
                size="large"
                color="secondary"
                variant="contained"
                disableElevation={true}
                startIcon={<PlayArrowOutlinedIcon />}
                onClick={handleSubmit(onSubmit)}
                disabled={compareButtonDisabled}
              >
                Compare
              </Button>
            </Stack>
          </Box>
        </Stack>
      )}
    </PageContainer>
  );
};

export default CompareQuotes;
